/* ──────────────────────────────────────────────────────────────────────────
 * Apollo Bearing Index — API client (IFX.api)
 * assets/interchange-api.js
 *
 * One fetch wrapper for every endpoint:
 *   - per-channel AbortController: a new request on the same channel
 *     ("search", "ac", "oem", "decode") aborts the previous one, which
 *     replaces the old searchSeq/acSeq stale guards.
 *   - 429 → one retry after Retry-After (or 1.2 s); 5xx/network → throws
 *     an Error with .status and .userMessage.
 *   - small in-memory caches for stats, popular, catalogs, the glossary and
 *     autocomplete prefixes (session only).
 * ───────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";
  var IFX = window.IFX;
  if (!IFX || IFX.api) return;

  var base = "";
  var controllers = {};
  var cache = { stats: null, popular: null, catalogs: null, glossary: null, ac: {} };
  var AC_CACHE_MAX = 200;

  function configure(apiBase) { base = String(apiBase || "").replace(/\/+$/, ""); }

  function buildUrl(path, params) {
    var url = new URL(base + path, window.location.origin);
    Object.keys(params || {}).forEach(function (k) {
      var v = params[k];
      if (v == null || v === "") return;
      url.searchParams.set(k, String(v));
    });
    return url.toString();
  }

  function abortChannel(channel) {
    if (channel && controllers[channel]) {
      try { controllers[channel].abort(); } catch (err) { /* noop */ }
      delete controllers[channel];
    }
  }

  function isAbort(err) { return err && (err.name === "AbortError" || err.aborted); }

  function request(path, params, opts) {
    opts = opts || {};
    var channel = opts.channel || null;
    var ctrl = null;
    if (channel) {
      abortChannel(channel);
      ctrl = typeof AbortController === "function" ? new AbortController() : null;
      if (ctrl) controllers[channel] = ctrl;
    }
    var init = { credentials: "omit", signal: ctrl ? ctrl.signal : undefined };
    if (opts.method) init.method = opts.method;
    if (opts.body != null) {
      init.headers = { "Content-Type": "application/json" };
      init.body = JSON.stringify(opts.body);
    }
    var url = buildUrl(path, params);
    var attempt = 0;
    function go() {
      attempt += 1;
      return fetch(url, init).then(function (r) {
        if (r.status === 429 && attempt < 2) {
          var wait = Number(r.headers.get("Retry-After")) * 1000 || 1200;
          return new Promise(function (resolve) { setTimeout(resolve, wait); }).then(go);
        }
        return r.json().catch(function () { return null; }).then(function (data) {
          if (!r.ok) {
            var e = new Error("API " + r.status);
            e.status = r.status;
            e.data = data;
            e.userMessage = data && data.message ? String(data.message)
              : r.status === 429 ? "Too many searches at once. Wait a moment and try again."
              : r.status >= 500 ? "The search service had a problem. Try again in a moment."
              : "That request could not be completed.";
            throw e;
          }
          if (channel && controllers[channel] === ctrl) delete controllers[channel];
          return data;
        });
      });
    }
    return go().catch(function (err) {
      if (isAbort(err)) { err.aborted = true; throw err; }
      if (!err.status) err.userMessage = err.userMessage || "We couldn't reach the search service. Check your connection and try again.";
      throw err;
    });
  }

  var api = {
    configure: configure,
    base: function () { return base; },
    isAbort: isAbort,
    abort: abortChannel,
    search: function (params) { return request("/api/search", params, { channel: "search" }); },
    family: function (baseKey, limit) { return request("/api/family", { base: baseKey, limit: limit }, { channel: "search" }); },
    intent: function (q) { return request("/api/intent", { q: q }, { channel: "intent" }); },
    autocomplete: function (q, limit) {
      var key = String(q).toUpperCase() + "|" + (limit || 8);
      if (cache.ac[key]) return Promise.resolve(cache.ac[key]);
      return request("/api/autocomplete", { q: q, limit: limit || 8 }, { channel: "ac" }).then(function (data) {
        var keys = Object.keys(cache.ac);
        if (keys.length > AC_CACHE_MAX) delete cache.ac[keys[0]];
        cache.ac[key] = data;
        return data;
      });
    },
    stats: function () {
      if (cache.stats) return Promise.resolve(cache.stats);
      return request("/api/stats").then(function (d) { cache.stats = d; return d; });
    },
    popular: function (limit) {
      if (cache.popular) return Promise.resolve(cache.popular);
      return request("/api/popular", { limit: limit || 12 }).then(function (d) { cache.popular = d; return d; });
    },
    compare: function (ids) { return request("/api/compare", { ids: ids.join(",") }, { channel: "compare" }); },
    decode: function (params) { return request("/api/decode", params, { channel: "decode" }); },
    glossary: function () {
      if (cache.glossary) return Promise.resolve(cache.glossary);
      return request("/api/decode").then(function (d) { cache.glossary = d; return d; });
    },
    equipment: function (params, channel) { return request("/api/equipment", params, { channel: channel || "oem" }); },
    catalogs: function () {
      if (cache.catalogs) return Promise.resolve(cache.catalogs);
      return request("/api/catalogs").then(function (d) { cache.catalogs = d; return d; });
    },
    rfq: function (body) { return request("/api/rfq", null, { method: "POST", body: body }); },
    warm: function () {
      // Idle warm-up of the two calls the hero needs; failures are silent.
      var run = function () { api.stats().catch(function () {}); api.popular().catch(function () {}); };
      if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(run, { timeout: 2500 });
      else setTimeout(run, 800);
    },
  };

  IFX.api = api;
  IFX.define("api", api);
})();
